<template>
  <SidebarMenu
    class="visit-inspection-sidebar"
    :show="showNavSidebar"
    @showChanged="showNavSidebar = $event"
    width="280px"
  >
    <div>
      <div class="visit-inspection-sidebar__header border">
        <h3>Form Section Navigation</h3>
        <button
          v-if="showDisclaimer"
          class="visit-inspection-sidebar__disclaimer"
          type="button"
          title="View note"
          @click="showDisclaimerModal = true"
        >
          <font-awesome-icon icon="fa-solid fa-circle-exclamation" />
        </button>
      </div>

      <div class="visit-inspection-sidebar__section-controls">
        <button class="visit-inspection-sidebar__control-btn" type="button" @click="toggleAllSections">
          <font-awesome-icon :icon="allSectionsExpanded ? 'fa-solid fa-minus-circle' : 'fa-solid fa-plus-circle'" />
          {{ allSectionsExpanded ? "Collapse" : "Expand" }} All Sections
        </button>
        <button v-if="!isHistoricalForm" class="visit-inspection-sidebar__control-btn" type="button" @click="togglePreviousResponses">
          <font-awesome-icon
            :icon="inspectionStore.previousResponsesExpanded ? 'fa-solid fa-minus-circle' : 'fa-solid fa-plus-circle'"
          />
          {{ inspectionStore.previousResponsesExpanded ? "Collapse" : "Expand" }} Previous Responses
        </button>
        <button v-if="saveAllowed" class="visit-inspection-sidebar__control-btn" type="button" @click="applyNAToAll">
          <font-awesome-icon icon="fa-solid fa-minus-circle" />
          Apply N/A to All Questions
        </button>
        <button
          v-if="saveAllowed"
          class="visit-inspection-sidebar__control-btn visit-inspection-sidebar__control-btn--danger"
          type="button"
          @click="clearAllAnswers"
        >
          <font-awesome-icon icon="fa-solid fa-trash" />
          Clear All Answers
        </button>
      </div>
      <ul>
        <li v-for="sectionEntry in sortedSections" :key="sectionEntry[0]">
          <a
            class="visit-inspection-sidebar__section-link"
            :class="{
              active: inspectionStore.activeSectionId === sectionEntry[0],
              'has-error': sectionsWithErrors.has(sectionEntry[1].name),
            }"
            @click.prevent="expandSection(sectionEntry[0])"
          >
            <!-- <div class="visit-inspection-sidebar__section-number">{{ sectionEntry[1].number }}</div> -->
            <div class="visit-inspection-sidebar__section-name">
              {{ sectionEntry[1].name }}
              <font-awesome-icon
                v-if="sectionsWithErrors.has(sectionEntry[1].name)"
                icon="fa-solid fa-exclamation-circle"
                class="visit-inspection-sidebar__error-icon"
              />
            </div>
          </a>
          <!-- <ul v-if="item.children">
          <li v-for="child in item.children" :key="child.id">
            <a :href="`#${child.id}`" @click.prevent="child.callback">{{ child.label }}</a>
          </li>
        </ul> -->
        </li>
      </ul>
    </div>
    <hr class="section-divider" height="1" width="100%" />
    <div>
      <div class="visit-inspection-sidebar__header">
        <h3>Quick actions</h3>
      </div>
      <div class="visit-inspection-sidebar__options">
        <ToggleSwitch
          :modelValue="showAnnualQuestions"
          @update:modelValue="$emit('update:showAnnualQuestions', $event)"
          :disabled="!hideAnnualQuestionsAllowed"
          text="Show Annual Questions"
        />
      </div>
      <div class="visit-inspection-sidebar__actions">
        <IconButton v-if="showPreviousButton" label="Previous" icon="fa-arrow-left" @click="onPreviousClicked" />
        <IconButton v-if="plantId" label="Plant History" icon="fa-history" @click="openPlantHistory" />
        <IconButton label="Print Form" icon="fa-print" @click="print" />
        <LoadingIconButton
          v-if="saveAllowed"
          :loading="formSubmitting || savingForm"
          label="Save Form"
          icon="fa-save"
          :disabled="formSubmitted"
          @click="onSaveClicked"
        />
        <LoadingIconButton
          type="submit"
          :loading="formSubmitting || savingForm"
          label="Submit Form"
          icon="fa-check"
          themeColor="primary"
          :disabled="formSubmitted"
          @click="onSubmit"
        />
      </div>
      <div class="visit-inspection-sidebar__last-saved-row">
        <div v-if="lastSavedDate" class="visit-inspection-sidebar__last-saved">
          <font-awesome-icon icon="fa-solid fa-clock" />
          <span>Saved {{ moment(lastSavedDate).fromNow() }}</span>
        </div>
      </div>
    </div>
  </SidebarMenu>

  <ModalDialog
    v-if="showDisclaimerModal"
    header-text="Note"
    :open="showDisclaimerModal"
    @closeModal="showDisclaimerModal = false"
  >
    <template #content>
      <p>
        Any question that refers to <em>"as indicated above"</em> applies to the response given
        either on this form or in any previous inspection response submitted this year for this plant.
      </p>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useInspectionStore } from "@/stores/useInspectionStore";
import moment from "moment";
import IconButton from "@/shared/ui/inputs/IconButton.vue";
import LoadingIconButton from "@/shared/ui/inputs/LoadingIconButton.vue";
import ToggleSwitch from "@/shared/ui/switch/ToggleSwitch.vue";
import { EnumVisitMode } from "@/Domain/EnumVisitMode";
import { useQueryParams } from "@/shared/helpers/useQueryParams";
import SidebarMenu from "@/shared/ui/layout/SidebarMenu.vue";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";

const inspectionStore = useInspectionStore();

const props = defineProps<{
  formSubmitting: boolean;
  savingForm: boolean;
  formSubmitted: boolean;
  saveAllowed: boolean;
  lastSavedDate: Date | undefined;
  showPreviousButton: boolean;
  showAnnualQuestions: boolean;
  hideAnnualQuestionsAllowed: boolean;
  sectionsWithErrors: Set<string>;
  plantId?: number;
  showDisclaimer?: boolean;
  isHistoricalForm?: boolean;
}>();

const emit = defineEmits<{
  (e: "saveClicked"): void;
  (e: "previousClicked"): void;
  (e: "update:showAnnualQuestions", value: boolean): void;
  (e: "applyNAToAll", value: boolean): void;
  (e: "clearAllAnswers"): void;
}>();

const { getParam } = useQueryParams();

// TODO: should account for the display order of sections
const sortedSections = computed(() => {
  return Object.entries(inspectionStore.sections).sort(([, a], [, b]) => (a.number ?? 0) - (b.number ?? 0));
});

const showNavSidebar = ref<boolean>(false);
const showDisclaimerModal = ref<boolean>(false);
const allSectionsExpanded = ref(true);

const expandSection = (sectionId: string) => {
  handleMobileSidebar();
  inspectionStore.expandSection(sectionId, true);
};

const toggleAllSections = () => {
  handleMobileSidebar();
  if (allSectionsExpanded.value) {
    inspectionStore.collapseAllSections();
  } else {
    inspectionStore.expandAllSections();
  }
  allSectionsExpanded.value = !allSectionsExpanded.value;
};

const togglePreviousResponses = () => {
  handleMobileSidebar();
  inspectionStore.togglePreviousResponses();
};

const applyNAToAll = () => {
  handleMobileSidebar();
  emit("applyNAToAll", true);
};

const clearAllAnswers = () => {
  if (!confirm("Are you sure you want to clear all answers from this form? This cannot be undone.")) return;
  handleMobileSidebar();
  emit("clearAllAnswers");
};

const onSaveClicked = () => {
  handleMobileSidebar();
  emit("saveClicked");
};

const menuUserRole = getParam("menu", "VisitsAsRfr");
const type = EnumVisitMode[getParam("type", EnumVisitMode.Inspection) as keyof typeof EnumVisitMode];

const onPreviousClicked = () => {
  window.location.href = `RfrVisitsSearch?type=${EnumVisitMode[type]}&menu=${menuUserRole}`;
};

const onSubmit = () => {
  inspectionStore.expandAllSections();
  handleMobileSidebar();
};

// Close sidebar on mobile after selecting an action
const handleMobileSidebar = () => {
  if (window.innerWidth < 576) {
    showNavSidebar.value = false;
  }
};

const print = () => {
  handleMobileSidebar();
  inspectionStore.expandAllSections();
  // Small delay to allow sidebar to close and sections to expand before printing
  setTimeout(() => {
    window.print();
  }, 300);
};

const openPlantHistory = () => {
  window.open(`/oudirect/PlantHistory/${props.plantId}`, "_blank");
};
</script>

<style lang="scss" scoped>
.visit-inspection-sidebar {
  &__header {
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &.border {
      border-bottom: 2px solid #0056b3;
      padding-bottom: 8px;
    }

    h3 {
      margin: 0;
      color: #333;
    }

    p {
      margin: 5px 0 0;
      font-size: 14px;
      color: #666;
    }
  }

  &__section-controls {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0px 0 8px;
    border-bottom: 1px solid #e9ecef;
  }

  &__control-btn {
    background: none;
    border: none;
    color: #0056b3;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    line-height: 1;

    svg {
      width: 14px;
      height: 14px;
      vertical-align: middle;
    }

    &:hover {
      background-color: #f0f0f0;
      color: #003d80;
    }

    &:active {
      transform: scale(0.95);
    }

    &--danger {
      color: #c0392b;

      &:hover {
        background-color: #fdecea;
        color: #922b21;
      }
    }
  }

  &__control-divider {
    color: #ccc;
    font-size: 14px;
    user-select: none;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 8px 0;

    li {
      margin-bottom: 2px;

      a {
        color: #0056b3;
        text-decoration: none;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 8px;
        border-radius: 4px;
        transition: background-color 0.2s ease;

        &:hover {
          background-color: #f5f5f5;
          color: #003366;
        }

        &.active {
          background-color: #e6f0ff;
          font-weight: bold;
        }
      }
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;

    > * {
      flex: 1;
      flex-basis: calc(50% - 4px);
    }
  }

  &__options {
    margin-bottom: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__last-saved-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
  }

  &__last-saved {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    padding: 8px 12px;
    background-color: #f8f9fa;
    border-radius: 6px;
    font-size: 13px;
    color: #495057;

    svg {
      color: #0056b3;
      width: 14px;
      height: 14px;
    }

    span {
      font-weight: 500;
    }
  }

  &__disclaimer {
    background: none;
    border: none;
    cursor: pointer;
    border-radius: 6px;
    color: #e67e22;
    font-size: 16px;
    line-height: 1;
    display: flex;
    align-items: center;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;

    &:hover {
      background-color: #fef3e2;
      color: #c0392b;
    }
  }

  &__section-number {
    background-color: #0056b3;
    color: #fff;
    border-radius: 50%;
    min-width: 24px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 14px;
  }

  &__section-name {
    font-size: 16px;
    color: #333;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex: 1;
  }

  &__error-icon {
    color: #dc3545;
    font-size: 14px;
    flex-shrink: 0;
  }

  .section-divider {
    margin-bottom: 20px;
  }
}
</style>
